import { createContext, useCallback, useContext, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const SELECTED_METHOD_PARAM = 'paymentMethod';

/** The id of the method the buyer picked, and the way to move it. */
export type SelectedMethodStore = readonly [string | undefined, (id: string | undefined) => void];

const SelectedMethodContext = createContext<SelectedMethodStore | null>(null);

export const SelectedMethodProvider = SelectedMethodContext.Provider;

/**
 * Reads the selection from whoever is holding it above. Routing is opt-in — the default install has
 * no router at all — so with nobody providing one the selection lives in component state and simply
 * does not survive a reload.
 */
export const useSelectedMethodId = (): SelectedMethodStore => {
  const provided = useContext(SelectedMethodContext);
  const local = useState<string | undefined>(undefined);

  return provided ?? local;
};

/**
 * Keeps the selection in the URL. Only call it under a router — that is what the provider above is
 * for. Under `hash` routing this is what carries the method across a reload; under `memory` the URL
 * never reaches the browser, so it only survives navigation inside the checkout.
 */
export const useSelectedMethodIdInUrl = (): SelectedMethodStore => {
  const [params, setParams] = useSearchParams();

  const setId = useCallback(
    (id: string | undefined) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (id) {
            next.set(SELECTED_METHOD_PARAM, id);
          } else {
            next.delete(SELECTED_METHOD_PARAM);
          }

          return next;
        },
        // The method is not a step of its own: going back should leave the payment page, not undo
        // the pick.
        { replace: true },
      ),
    [setParams],
  );

  const methodId = params.get(SELECTED_METHOD_PARAM) ?? undefined;

  return [methodId, setId];
};
